import React from "react";
import { FaAws, FaNodeJs, FaReact } from "react-icons/fa";
import { RiGithubLine } from "react-icons/ri";
import { SiPostgresql, SiTypescript } from "react-icons/si";
import HeroConstellation from "./HeroConstellation";
import { siteContent, socialLinks, type HeroSkillTone } from "../content/site";
import "./HeroSection.css";

type HeroSectionProps = {
  constellationLayer?: React.ReactNode;
};

const skillIcons = {
  react: FaReact,
  typescript: SiTypescript,
  nodejs: FaNodeJs,
  postgresql: SiPostgresql,
  aws: FaAws,
} as const;

const skillToneClasses: Record<HeroSkillTone, string> = {
  library: "hero-skill-icon-tone-library",
  language: "hero-skill-icon-tone-language",
  runtime: "hero-skill-icon-tone-runtime",
  database: "hero-skill-icon-tone-database",
  cloud: "hero-skill-icon-tone-cloud",
};

const HeroSection: React.FC<HeroSectionProps> = ({ constellationLayer }) => {
  const { hero, work } = siteContent;
  const githubLink = socialLinks.find((link) => link.platform === "GitHub");
  const resolvedConstellationLayer =
    constellationLayer ?? <HeroConstellation density="low" minWidth={640} motionScale={0.55} opacity={0.42} />;

  const scrollToWork = () => {
    const workSection = document.getElementById(work.sectionId);

    if (!workSection) {
      return;
    }

    workSection.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="hero-section" id={hero.sectionId}>
      <div className="hero-ambient-layer" aria-hidden="true">
        <span className="hero-ambient-glow hero-ambient-glow-one" />
        <span className="hero-ambient-glow hero-ambient-glow-two" />
        <span className="hero-ambient-glow hero-ambient-glow-three" />
      </div>
      {resolvedConstellationLayer ? (
        <div className="hero-motion-layer" aria-hidden="true">
          {resolvedConstellationLayer}
        </div>
      ) : null}

      <div className="hero-shell">
        <div className="hero-content-layer">
          <div className="hero-copy">
            <p className="hero-kicker">
              <span>Canada-Based</span>
              <span>Open to Remote</span>
            </p>
            <h1 className="hero-title">{hero.title}</h1>
            <p className="hero-intro">{hero.intro}</p>

            <div className="hero-actions" aria-label="Hero actions">
              <button type="button" className="button-primary hero-action-button" onClick={scrollToWork}>
                View Work
              </button>
              {githubLink ? (
                <a
                  href={githubLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={githubLink.ariaLabel}
                  className="button-secondary hero-action-button hero-action-link"
                >
                  <RiGithubLine size={18} />
                  <span>GitHub</span>
                </a>
              ) : null}
            </div>
          </div>
        </div>

        <div className="hero-portrait-layer">
          <div className="hero-portrait-wrap">
            <div className="hero-portrait-frame">
              <div className="hero-portrait animated-picture">
                <img src={hero.image.src} alt={hero.image.alt} className="hero-image" />
              </div>
            </div>
            <div className="hero-stack-card hero-stack-card-under-portrait" aria-label="Core skills">
              <p className="hero-stack-label">{hero.coreStackLabel}</p>
              <div className="hero-stack-inline">
                {hero.coreStack.map((skill) => {
                  const Icon = skillIcons[skill.icon];
                  const toneClassName = skillToneClasses[skill.tone];

                  return (
                    <span key={skill.name} className="hero-stack-item">
                      <Icon className={`hero-skill-icon ${toneClassName}`} aria-hidden="true" />
                      <span>{skill.name}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
